import styled, { css } from 'styled-components';
import { Formik, Form, FormikProps, FormikValues } from 'formik';
import { useEffect, useRef } from 'react';

import { Button } from 'shared/components/Button';
import { FitFeedbackFormApparel } from 'productTemplate/components/fitFeedback/FitFeedbackFormApparel';
import { FitFeedbackFormSneakers } from 'productTemplate/components/fitFeedback/FitFeedbackFormSneakers';
import { FitFeedbackFormUserInfo } from 'productTemplate/components/fitFeedback/FitFeedbackFormUserInfo';
import { getFitFeedbackFormValidationSchema } from 'productTemplate/utils/getFitFeedbackFormValidationSchema';
import {
  INITIAL_APPAREL_FIT_FEEDBACK_FORM_STATE,
  INITIAL_SNEAKERS_FIT_FEEDBACK_FORM_STATE,
} from 'productTemplate/state/fitFeedbackFormReducer';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useFitFeedback } from 'productTemplate/hooks/useFitFeedback';
import { useFitFeedbackFormState } from 'productTemplate/providers/FitFeedbackProvider';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { t } from 'localization/utils/t';
import { Window } from 'shared/components/Window';
import { variableFont, variableFontMedium } from 'styles/theme';
import { hideScrollbars } from 'styles/utils/hideScrollbars';

const TP = 'productTemplate.components.fitFeedback.FitFeedbackFormWindow';

export const FitFeedbackFormWindow = () => {
  const { productCategory } = useStoreSelector((state) => state.pdp.productTemplate);
  const { errorMessage, handleCloseFitFeedbackForm, handleFitClick, handleFormSubmit } =
    useFitFeedback();
  const { heightMeasurement } = useFitFeedbackFormState();
  const {
    isMobile,
    windowSize: { width },
  } = useWindowSize();
  const formikFormRef = useRef<FormikProps<FormikValues>>(null);
  const isSneakers = productCategory === 'shoes';

  const handleClose = () => {
    handleCloseFitFeedbackForm();
    handleFitClick();
  };

  const handleWheel = (e) => {
    e.stopPropagation();
  };

  useEffect(() => {
    sendTrackingEvent('MODAL_OPEN_VIEW', { modal_name: 'feedback_on_fit' });
  }, []);

  return (
    <FitFeedbackFormWindow.Window
      id="fit"
      isPermanent
      onClose={handleClose}
      onWheel={handleWheel}
      initialPermanentPosition={!isMobile && { bottom: 0, left: width - 365 }}
      title={t(`${TP}.formTitle`, 'Feedback on Fit')}
      closeType="text"
    >
      <div className="body">
        <p>
          {t(
            `${TP}.formInstructions`,
            'Your feedback is anonymous and helps inform our community of the fit of this item',
          )}
        </p>
        <Formik
          initialValues={
            isSneakers
              ? INITIAL_SNEAKERS_FIT_FEEDBACK_FORM_STATE
              : INITIAL_APPAREL_FIT_FEEDBACK_FORM_STATE
          }
          innerRef={formikFormRef}
          onSubmit={handleFormSubmit}
          validationSchema={getFitFeedbackFormValidationSchema(isSneakers, heightMeasurement)}
        >
          {({
            dirty,
            errors,
            handleBlur,
            handleSubmit,
            isSubmitting,
            isValid,
            setFieldValue,
            touched,
          }) => (
            <Form>
              {isSneakers ? (
                <FitFeedbackFormSneakers
                  setFieldValue={setFieldValue}
                  errors={errors}
                  touched={touched}
                />
              ) : (
                <>
                  <FitFeedbackFormUserInfo
                    errors={errors}
                    onBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    touched={touched}
                  />
                  <hr />
                  <FitFeedbackFormApparel
                    setFieldValue={setFieldValue}
                    errors={errors}
                    touched={touched}
                  />
                </>
              )}

              <div className="bottomGradient" />
              <FitFeedbackFormWindow.SubmitButton
                data-qa="submit_fit_feedback"
                onClick={() => handleSubmit()}
                disabled={isSubmitting}
                $appearDisabled={!(isValid && dirty)}
              >
                {t(`${TP}.submitFeedback`, 'Submit Feedback')}
              </FitFeedbackFormWindow.SubmitButton>
            </Form>
          )}
        </Formik>
        {errorMessage && <div className="error">{errorMessage}</div>}
      </div>
    </FitFeedbackFormWindow.Window>
  );
};

FitFeedbackFormWindow.Window = styled(Window)`
  max-height: 420px;
  z-index: ${({ theme }) => theme.zIndex.fitFeedbackWindow} !important;
  overflow: hidden;
  ${variableFont({ height: '12px', size: '12px' })}

  h2 {
    ${variableFontMedium({ height: '12px', size: '12px' })}
  }

  p {
    margin-bottom: 40px;
    ${variableFont({ height: '12px', size: '12px' })}
  }

  .body {
    padding: 0 16px 16px;
    overflow: auto;
    ${hideScrollbars};
  }

  hr {
    margin: 0 -16px;
  }

  .error {
    color: ${({ theme }) => theme.colors.red};
    text-align: center;
    margin: 8px 0;
  }

  .topGradient,
  .bottomGradient {
    position: sticky;
    width: 100%;
  }

  .topGradient {
    top: 0;
    height: 24px;
    width: 100%;
    z-index: ${({ theme }) => theme.zIndex.mobilePdpHeaderGradient};
    background: linear-gradient(to bottom, white, transparent);
  }

  .bottomGradient {
    bottom: -16px;
    height: 52px;
    margin-top: -12px;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 100%);
    z-index: ${({ theme }) => theme.zIndex.mobilePdpHeaderGradient};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.small - 1}px) {
    max-height: 500px;
    margin-left: 16px;
    margin-right: 16px;
    max-width: ${({ theme }) => theme.breakpoints.small}px;
    margin-top: 10vh;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall - 1}px) {
    max-height: 500px;
    position: absolute;
    right: 66px;
    bottom: 0;
    margin-top: 10vh;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.medium}px) {
    h2 {
      ${variableFontMedium({ height: '12.5px', size: '12.5px' })}
    }
    p {
      ${variableFont({ size: '12.5px' })}
    }
  }
`;

FitFeedbackFormWindow.SubmitButton = styled(Button)<{ $appearDisabled: boolean }>`
  bottom: -3px;
  cursor: pointer;
  position: sticky;
  text-transform: none;
  border-radius: ${({ theme }) => theme.borderRadii.button};
  z-index: ${({ theme }) => theme.zIndex.actionButton};

  ${({ $appearDisabled, theme }) =>
    $appearDisabled &&
    css`
      color: ${theme.colors.black} !important;
      cursor: default !important;
      border: ${theme.borders.black};
      background-color: ${theme.colors.white};
      &:focus {
        background-color: ${theme.colors.white};
      }
    `}
`;
